import { useParams, useNavigate } from "react-router-dom";
import { TournamentForm } from "./components/TournamentForm";
import { tournamentService } from "@/frontend/services";
import type { TournamentResponseDTO } from "@/shared/dto";
import { useGetId } from "@/frontend/hooks/useGetId";
import { Spin } from "@/frontend/components/common";

export function UpdateTournamentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: tournamentData, loading: tournamentLoading } =
    useGetId<TournamentResponseDTO>({
      fetchFn: tournamentService.getById,
      redirectUrl: "/tournaments",
      errorMessage: "Error al cargar el torneo",
      id,
    });

  if (tournamentLoading) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm w-full">
        <div className="flex justify-center items-center py-12">
          <Spin size="large" tip="Cargando..." />
        </div>
      </div>
    );
  }

  if (!tournamentData) return null;

  if (!tournamentData.canEdit) {
    navigate("/tournaments");
    return null;
  }

  const initialValues = {
    name: tournamentData.name,
    discipline: tournamentData.discipline
      ? {
          id: tournamentData.discipline.id,
          name: tournamentData.discipline.name,
        }
      : undefined,
    description: tournamentData.description || "",
    sortOrder: tournamentData.sortOrder ?? undefined,
    country: tournamentData.country
      ? { id: tournamentData.country.id, name: tournamentData.country.name }
      : undefined,
    configuration: {
      hasReferees: tournamentData.configuration?.hasReferees ?? false,
      hasLineups: tournamentData.configuration?.hasLineups ?? false,
      hasStadium: tournamentData.configuration?.hasStadium ?? false,
      hasTeams: tournamentData.configuration?.hasTeams ?? true,
      hasSubstitutions: tournamentData.configuration?.hasSubstitutions ?? false,
      hasCards: tournamentData.configuration?.hasCards ?? false,
      maxPlayersPerTeam: tournamentData.configuration?.maxPlayersPerTeam,
      minPlayersPerTeam: tournamentData.configuration?.minPlayersPerTeam,
      eventTypes: (tournamentData.configuration?.eventTypes || []).join(","),
    },
    statusIds: (tournamentData.statusIds as string[]) || [],
    statusId:
      tournamentData.statusIds && tournamentData.statusIds.length > 0
        ? tournamentData.statusIds[0]
        : undefined,
    seasons: tournamentData.seasons?.map((s) => ({
      season: s.season,
      start: s.start ? new Date(s.start).toISOString().slice(0, 10) : undefined,
      end: s.end ? new Date(s.end).toISOString().slice(0, 10) : undefined,
      statusId: s.status?.id,
      status: s.status
        ? {
            id: s.status.id,
            name: s.status.name,
            slug: s.status.slug,
            color: s.status.color || undefined,
            isActive: s.status.isActive,
            sortOrder: s.status.sortOrder,
          }
        : null,
      isCurrent: s.isCurrent ?? false,
    })),
    newSeasons: [],
  } as any;

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm w-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Actualizar Torneo</h2>
        <p className="text-gray-600 mt-1">Modifica la información del torneo</p>
      </div>
      <TournamentForm tournamentId={id} initialValues={initialValues} />
    </div>
  );
}
